import { useEffect } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Slider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useForm, useWatch, type Path } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import Modal from '@/components/common/modal';
import { useCreateFeatureMutation } from '@/state/mutations/features/use-create-feature';
import { useUpdateFeatureMutation } from '@/state/mutations/features/use-update-feature';
import { useTranslations } from '@/i18n/use-translations';
import type { Feature, CreateFeatureDto, UpdateFeatureDto } from '@/types/features';

const GLOBAL_ROLES = ['basic_user', 'beta_user', 'internal_user', 'admin'] as const;

const createFeatureSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  displayName: z.string().min(1, 'Display name is required'),
  description: z.string().optional(),
  enabledRoles: z.array(z.string()).default([]),
  enabledForAll: z.boolean().default(false),
  abTesting: z
    .object({
      enabled: z.boolean(),
      percentage: z.number().min(0).max(100),
    })
    .optional(),
});

const updateFeatureSchema = createFeatureSchema.omit({ name: true });

type CreateFeatureFormData = z.infer<typeof createFeatureSchema>;
type UpdateFeatureFormData = z.infer<typeof updateFeatureSchema>;

interface FeatureFormModalProps {
  open: boolean;
  onClose: () => void;
  feature: Feature | null;
}

export default function FeatureFormModal({ open, onClose, feature }: FeatureFormModalProps) {
  const t = useTranslations();
  const isEditing = !!feature;
  const createMutation = useCreateFeatureMutation();
  const updateMutation = useUpdateFeatureMutation();

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<CreateFeatureFormData | UpdateFeatureFormData>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(isEditing ? updateFeatureSchema : createFeatureSchema) as any,
    defaultValues: isEditing
      ? {
          displayName: '',
          description: '',
          enabledRoles: [],
          enabledForAll: false,
          abTesting: { enabled: false, percentage: 0 },
        }
      : {
          name: '',
          displayName: '',
          description: '',
          enabledRoles: [],
          enabledForAll: false,
          abTesting: { enabled: false, percentage: 0 },
        },
  });

  const enabledRoles = useWatch({ control, name: 'enabledRoles', defaultValue: [] });
  const enabledForAll = useWatch({ control, name: 'enabledForAll', defaultValue: false });
  const abTesting = useWatch({ control, name: 'abTesting' });

  useEffect(() => {
    if (open) {
      if (feature) {
        reset({
          displayName: feature.displayName,
          description: feature.description || '',
          enabledRoles: feature.enabledRoles || [],
          enabledForAll: feature.enabledForAll || false,
          abTesting: feature.abTesting || { enabled: false, percentage: 0 },
        } as UpdateFeatureFormData);
      } else {
        reset({
          name: '',
          displayName: '',
          description: '',
          enabledRoles: [],
          enabledForAll: false,
          abTesting: { enabled: false, percentage: 0 },
        } as CreateFeatureFormData);
      }
    }
  }, [open, feature, reset]);

  const onSubmit = async (data: CreateFeatureFormData | UpdateFeatureFormData) => {
    try {
      if (isEditing && feature) {
        const updateData: UpdateFeatureDto = {
          displayName: data.displayName,
          description: data.description,
          enabledRoles: data.enabledRoles,
          enabledForAll: data.enabledForAll,
          abTesting: data.abTesting,
        };
        await updateMutation.mutateAsync({ id: feature.id, data: updateData });
      } else {
        const createData = data as CreateFeatureFormData;
        const createPayload: CreateFeatureDto = {
          name: createData.name,
          displayName: createData.displayName,
          description: createData.description,
          enabledRoles: createData.enabledRoles,
          enabledForAll: createData.enabledForAll,
          abTesting: createData.abTesting,
        };
        await createMutation.mutateAsync(createPayload);
      }
      onClose();
    } catch (error) {
      console.error('Error saving feature:', error);
    }
  };

  const handleRoleToggle = (role: string) => {
    const currentRoles = enabledRoles || [];
    if (currentRoles.includes(role)) {
      setValue(
        'enabledRoles',
        currentRoles.filter((r: string) => r !== role),
      );
    } else {
      setValue('enabledRoles', [...currentRoles, role]);
    }
  };

  const handleAbTestingToggle = () => {
    setValue('abTesting', {
      enabled: !abTesting?.enabled,
      percentage: abTesting?.percentage || 0,
    });
  };

  return (
    <Modal
      open={open}
      handleClose={onClose}
      title={isEditing ? t('features.edit') || 'Edit Feature' : t('features.create') || 'Create Feature'}
      size="md"
    >
      <form
        onSubmit={handleSubmit(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onSubmit as any,
        )}
      >
        <Stack spacing={2}>
          {!isEditing && (
            <TextField
              label={t('features.name') || 'Name'}
              {...register('name' as Path<CreateFeatureFormData>)}
              error={!!(errors as Partial<Record<keyof CreateFeatureFormData, { message?: string }>>).name}
              helperText={(errors as Partial<Record<keyof CreateFeatureFormData, { message?: string }>>).name?.message}
              fullWidth
              required
            />
          )}

          <TextField
            label={t('features.displayName') || 'Display Name'}
            {...register('displayName')}
            error={!!errors.displayName}
            helperText={errors.displayName?.message as string}
            fullWidth
            required
          />

          <TextField
            label={t('features.description') || 'Description'}
            {...register('description')}
            error={!!errors.description}
            helperText={errors.description?.message as string}
            fullWidth
            multiline
            rows={3}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={enabledForAll}
                {...register('enabledForAll')}
                onChange={(e) => setValue('enabledForAll', e.target.checked)}
              />
            }
            label={t('features.enabledForAll') || 'Enabled For All'}
          />

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {t('features.enabledRoles') || 'Enabled Roles'}
            </Typography>
            <FormGroup>
              {GLOBAL_ROLES.map((role) => (
                <FormControlLabel
                  key={role}
                  control={
                    <Checkbox checked={enabledRoles?.includes(role) || false} onChange={() => handleRoleToggle(role)} />
                  }
                  label={role}
                />
              ))}
            </FormGroup>
          </Box>

          <Box>
            <FormControlLabel
              control={<Checkbox checked={abTesting?.enabled || false} onChange={handleAbTestingToggle} />}
              label={t('features.enableAbTesting') || 'Enable A/B Testing'}
            />

            {abTesting?.enabled && (
              <Box sx={{ mt: 2, pl: 4 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {t('features.percentage') || 'Percentage'} ({abTesting?.percentage || 0}%)
                </Typography>
                <Slider
                  value={abTesting?.percentage || 0}
                  onChange={(_, value) =>
                    setValue('abTesting', {
                      enabled: true,
                      percentage: value as number,
                    })
                  }
                  min={0}
                  max={100}
                  step={1}
                  marks={[
                    { value: 0, label: '0%' },
                    { value: 25, label: '25%' },
                    { value: 50, label: '50%' },
                    { value: 75, label: '75%' },
                    { value: 100, label: '100%' },
                  ]}
                />
              </Box>
            )}
          </Box>

          <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button variant="outlined" onClick={onClose}>
              {t('common.cancel') || 'Cancel'}
            </Button>
            <Button type="submit" variant="contained" disabled={createMutation.isPending || updateMutation.isPending}>
              {isEditing ? t('common.save') || 'Save' : t('common.create') || 'Create'}
            </Button>
          </Stack>
        </Stack>
      </form>
    </Modal>
  );
}
